'use client';

import * as React from 'react';

import { useMediaQuery } from '../../lib/hooks/use-media-query';
import { cn } from '../../lib/utils';

import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from './drawer';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from './sheet';

interface BaseProps {
	children: React.ReactNode;
}

interface RootPanelProps extends BaseProps {
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	modal?: boolean;
}

interface PanelProps extends BaseProps {
	className?: string;
	asChild?: true;
	side?: 'top' | 'right' | 'bottom' | 'left';
}

interface PanelContentProps extends PanelProps {
	onPointerDownOutside?: (event: Event) => void;
	onInteractOutside?: (event: Event) => void;
}

const mobile = '(max-width: 767px)';

// The resolved `isMobile` value is provided by the `Panel` root so that every
// subcomponent renders the correct (Drawer vs Sheet) variant on its first render.
// Without this, each subcomponent ran its own `useMediaQuery` and gated on
// `isInitializingState`, so nested pieces (e.g. PanelTitle inside PanelContent)
// resolved a render later than their parent. That made the Radix DialogContent
// mount before its DialogTitle existed, triggering the "DialogContent requires a
// DialogTitle" warning. `null` means there is no Panel ancestor (standalone use),
// in which case the subcomponent falls back to its own media query.
const PanelMediaContext = React.createContext<boolean | null>(null);

function usePanelIsMobile(): { value: boolean; isInitializingState: boolean } {
	const contextValue = React.useContext(PanelMediaContext);
	const local = useMediaQuery(mobile);

	if (contextValue !== null) return { value: contextValue, isInitializingState: false };
	return local;
}

const Panel = ({ children, ...props }: RootPanelProps) => {
	const { value: isMobile, isInitializingState } = useMediaQuery(mobile);

	if (isInitializingState) return null;
	const PanelRoot = isMobile ? Drawer : Sheet;

	return (
		<PanelMediaContext.Provider value={isMobile}>
			<PanelRoot {...props}>{children}</PanelRoot>
		</PanelMediaContext.Provider>
	);
};

const PanelTrigger = ({ className, children, ...props }: PanelProps) => {
	const { value: isMobile, isInitializingState } = usePanelIsMobile();

	if (isInitializingState) return null;
	const PanelTrigger = isMobile ? DrawerTrigger : SheetTrigger;

	return (
		<PanelTrigger className={className} {...props}>
			{children}
		</PanelTrigger>
	);
};

const PanelClose = ({ className, children, ...props }: PanelProps) => {
	const { value: isMobile, isInitializingState } = usePanelIsMobile();

	if (isInitializingState) return null;
	const PanelClose = isMobile ? DrawerClose : SheetClose;

	return (
		<PanelClose className={className} {...props}>
			{children}
		</PanelClose>
	);
};

const PanelContent = ({ className, children, side = 'right', ...props }: PanelContentProps) => {
	const { value: isMobile, isInitializingState } = usePanelIsMobile();

	if (isInitializingState) return null;
	const PanelContent = isMobile ? DrawerContent : SheetContent;

	return (
		<PanelContent className={cn(isMobile && 'h-[80vh]', 'p-0 md:max-w-xl', className)} side={isMobile ? 'bottom' : side} {...props}>
			<div className="h-full flex flex-col">{children}</div>
		</PanelContent>
	);
};

const PanelDescription = ({ className, children, ...props }: PanelProps) => {
	const { value: isMobile, isInitializingState } = usePanelIsMobile();

	if (isInitializingState) return null;
	const PanelDescription = isMobile ? DrawerDescription : SheetDescription;

	return (
		<PanelDescription className={className} {...props}>
			{children}
		</PanelDescription>
	);
};

const PanelHeader = ({ className, children, ...props }: PanelProps) => {
	const { value: isMobile, isInitializingState } = usePanelIsMobile();

	if (isInitializingState) return null;
	const PanelHeader = isMobile ? DrawerHeader : SheetHeader;

	return (
		<PanelHeader className={cn('flex-shrink-0', 'px-4', className)} {...props}>
			{children}
		</PanelHeader>
	);
};

const PanelTitle = ({ className, children, ...props }: PanelProps) => {
	const { value: isMobile, isInitializingState } = usePanelIsMobile();

	if (isInitializingState) return null;
	const PanelTitle = isMobile ? DrawerTitle : SheetTitle;

	return (
		<PanelTitle className={className} {...props}>
			{children}
		</PanelTitle>
	);
};

const PanelBody = ({ className, children, ...props }: PanelProps) => {
	const { value: isMobile, isInitializingState } = usePanelIsMobile();

	if (isInitializingState) return null;

	return (
		<div className={cn('flex-1 overflow-y-auto px-4', isMobile && 'max-h-[calc(80vh-8rem)]', className)} {...props}>
			{children}
		</div>
	);
};

const PanelFooter = ({ className, children, ...props }: PanelProps) => {
	const { value: isMobile, isInitializingState } = usePanelIsMobile();

	if (isInitializingState) return null;
	const PanelFooter = isMobile ? DrawerFooter : SheetFooter;

	return (
		<PanelFooter className={cn('flex-shrink-0', 'px-4', className)} {...props}>
			{children}
		</PanelFooter>
	);
};

export { Panel, PanelBody, PanelClose, PanelContent, PanelDescription, PanelFooter, PanelHeader, PanelTitle, PanelTrigger };
